import type { ComponentProps } from 'react';

import type { RouteObject } from '@suntex.core/nav';
import { RequireAuth } from '@suntex.fim/framework';
import { PlayerDashboard, PlayerPathRedirect } from '@suntex.fim/player';

import { PlayerLayout } from '../layouts/PlayerLayout';

import { PlayerDashboardRedirect } from './redirects/PlayerDashboardRedirect';

export interface CreatePlayerRoutesOptions {
  /** Chrome props forwarded to {@link PlayerLayout} (e.g. `appVersion`). */
  chrome: ComponentProps<typeof PlayerLayout>;
  /** App-specific pages mounted under `/player/*`. */
  playerPages?: RouteObject[];
}

/**
 * Builds the shared player route tree: bare `/player` redirects to the player's
 * dashboard, and `/player/*` is auth-gated and wrapped in {@link PlayerLayout}
 * with the shared `dashboard/*` route plus any app-specific pages. The player is
 * always derived from the login, so routes carry no `:playerId` segment; legacy
 * `/player/:playerId/*` links are normalized by {@link PlayerDashboardRedirect}.
 * Both apps consume this so player routing stays consistent.
 */
export function createPlayerRoutes({
  chrome,
  playerPages = [],
}: CreatePlayerRoutesOptions): RouteObject[] {
  const dashboardRoute: RouteObject = {
    path: 'dashboard/*',
    children: [
      { index: true, Component: PlayerDashboard },
      { path: '*', Component: PlayerPathRedirect },
    ],
  };
  const pages: RouteObject[] = [dashboardRoute, ...playerPages];

  return [
    {
      path: '/player',
      Component: PlayerDashboardRedirect,
    },
    {
      path: '/player/*',
      element: <RequireAuth />,
      children: [
        {
          element: <PlayerLayout {...chrome} />,
          children: [
            ...pages,
            {
              path: '*',
              Component: PlayerDashboardRedirect,
            },
          ],
        },
      ],
    },
  ];
}
